/* What the menu says, and what it is called.
 *
 * ERPNext has 468 openable DocTypes. Putting all of them in a menu is the
 * same as putting none of them there, and it is most of why people say the
 * desk is only for accountants. So the menu is **curated and in plain
 * words** -- "Money in" rather than "Accounts Receivable" -- and everything
 * else is reachable from Browse everything and the search box, so nothing is
 * hidden, only sorted.
 *
 * A name here is a label, never a translation: `Sales Invoice` is still a
 * Sales Invoice on the form and in the ledger, because a person who phones
 * their accountant has to be able to say what they are looking at.
 */
import index from './doctypes.generated.json'

export type Entry = { label: string; doctype?: string; report?: string
                      /** A screen of ours rather than a DocType or a report. */
                      screen?: string; hint?: string }
export type Section = { title: string; entries: Entry[] }

export const MENU: Section[] = [
  {
    title: 'Money in',
    entries: [
      { label: 'Invoices', doctype: 'Sales Invoice', hint: 'What customers owe you' },
      { label: 'Quotations', doctype: 'Quotation' },
      { label: 'Sales orders', doctype: 'Sales Order' },
      { label: 'Delivery notes', doctype: 'Delivery Note' },
      { label: 'Payments received', doctype: 'Payment Entry' },
      { label: 'Customers', doctype: 'Customer' },
      { label: 'Blanket orders', doctype: 'Blanket Order', hint: 'An agreed quantity, drawn down over time' },
    ],
  },
  {
    title: 'Money out',
    entries: [
      { label: 'Bills', doctype: 'Purchase Invoice', hint: 'What you owe suppliers' },
      { label: 'Purchase orders', doctype: 'Purchase Order' },
      { label: 'Goods received', doctype: 'Purchase Receipt' },
      { label: 'Material requests', doctype: 'Material Request' },
      { label: 'Suppliers', doctype: 'Supplier' },
      { label: 'Supplier quotations', doctype: 'Supplier Quotation' },
      { label: 'Quotation requests', doctype: 'Request for Quotation', hint: 'Ask several suppliers at once' },
    ],
  },
  {
    title: 'The books',
    entries: [
      { label: 'Chart of accounts', doctype: 'Account', hint: 'Every account in your ledger' },
      { label: 'Journal entries', doctype: 'Journal Entry' },
      { label: 'Ledger entries', doctype: 'GL Entry', hint: 'Every posting, newest first' },
      { label: 'Cost centres', doctype: 'Cost Center' },
      { label: 'Bank accounts', doctype: 'Bank Account' },
      { label: 'Bank transactions', doctype: 'Bank Transaction' },
      { label: 'GST for the month', screen: '/gst', hint: 'What gets filed, worked out from your invoices' },
      { label: 'Accounting periods', doctype: 'Accounting Period', hint: 'Close a month so nobody can post into it' },
      { label: 'Closing entries', doctype: 'Period Closing Voucher' },
      { label: 'Opening invoices', doctype: 'Opening Invoice Creation Tool', hint: 'What was outstanding on the day you started' },
    ],
  },
  {
    title: 'Reports',
    entries: [
      { label: 'General ledger', report: 'General Ledger', hint: 'Every posting, filtered' },
      { label: 'Trial balance', report: 'Trial Balance' },
      { label: 'Profit and loss', report: 'Profit and Loss Statement' },
      { label: 'Balance sheet', report: 'Balance Sheet' },
      { label: 'Money owed to you', report: 'Accounts Receivable', hint: 'Receivables, by age' },
      { label: 'Money you owe', report: 'Accounts Payable', hint: 'Payables, by age' },
      { label: 'Stock balance', report: 'Stock Balance' },
      { label: 'Stock ageing', report: 'Stock Ageing' },
      { label: 'What to reorder', report: 'Item Shortage Report' },
      { label: 'Sales by customer', report: 'Sales Analytics' },
      { label: 'Project profitability', report: 'Project Profitability' },
      { label: 'Asset register', report: 'Fixed Asset Register' },
      { label: 'Work order summary', report: 'Work Order Summary' },
      { label: 'All reports', report: '' },
    ],
  },
  {
    title: 'Stock',
    entries: [
      { label: 'Items', doctype: 'Item' },
      { label: 'Stock entries', doctype: 'Stock Entry' },
      { label: 'Stock ledger', doctype: 'Stock Ledger Entry' },
      { label: 'Warehouses', doctype: 'Warehouse' },
      { label: 'Stock reconciliation', doctype: 'Stock Reconciliation' },
      { label: 'Batches', doctype: 'Batch' },
      { label: 'Serial numbers', doctype: 'Serial No' },
      { label: 'Packing slips', doctype: 'Packing Slip' },
      { label: 'Deliveries out', doctype: 'Delivery Trip' },
      { label: 'Shipments', doctype: 'Shipment' },
    ],
  },
  {
    title: 'Making things',
    entries: [
      { label: 'Bills of materials', doctype: 'BOM', hint: 'What goes into what' },
      { label: 'Work orders', doctype: 'Work Order' },
      { label: 'Job cards', doctype: 'Job Card' },
      { label: 'Production plans', doctype: 'Production Plan' },
      { label: 'Workstations', doctype: 'Workstation' },
      { label: 'Operations', doctype: 'Operation' },
      { label: 'Routings', doctype: 'Routing', hint: 'The order the operations happen in' },
      { label: 'Swap a part everywhere', doctype: 'BOM Update Tool', hint: 'Replace one part with another across every BOM' },
      { label: 'BOM changes', doctype: 'BOM Update Log' },
      { label: 'Sent out to be made', doctype: 'Subcontracting Order' },
      { label: 'Back from the subcontractor', doctype: 'Subcontracting Receipt' },
    ],
  },
  {
    title: 'Assets & projects',
    entries: [
      { label: 'Assets', doctype: 'Asset' },
      { label: 'Asset movements', doctype: 'Asset Movement' },
      { label: 'Asset repairs', doctype: 'Asset Repair' },
      { label: 'Asset categories', doctype: 'Asset Category' },
      { label: 'Projects', doctype: 'Project' },
      { label: 'Tasks', doctype: 'Task' },
      { label: 'Timesheets', doctype: 'Timesheet' },
      { label: 'Activity types', doctype: 'Activity Type' },
    ],
  },
  {
    title: 'People & leads',
    entries: [
      { label: 'Leads', doctype: 'Lead' },
      { label: 'Prospects', doctype: 'Prospect', hint: 'A company you are working on, before it is a customer' },
      { label: 'Opportunities', doctype: 'Opportunity' },
      { label: 'Campaigns', doctype: 'Campaign' },
      { label: 'Everyone you deal with', screen: '/contacts', hint: 'One company, with its roles' },
      { label: 'Contacts', doctype: 'Contact' },
      { label: 'Addresses', doctype: 'Address' },
    ],
  },
  {
    title: 'Looking after customers',
    entries: [
      { label: 'Issues', doctype: 'Issue', hint: 'Something a customer has reported' },
      { label: 'Warranty claims', doctype: 'Warranty Claim' },
      { label: 'Servicing schedules', doctype: 'Maintenance Schedule' },
      { label: 'Service visits', doctype: 'Maintenance Visit' },
      { label: 'Installations', doctype: 'Installation Note' },
      { label: 'Service agreements', doctype: 'Service Level Agreement' },
    ],
  },
  {
    title: 'Quality',
    entries: [
      { label: 'Inspections', doctype: 'Quality Inspection', hint: 'Checked on the way in or out' },
      { label: 'What is checked', doctype: 'Quality Inspection Template' },
      { label: 'Goals', doctype: 'Quality Goal' },
      { label: 'Procedures', doctype: 'Quality Procedure' },
      { label: 'Reviews', doctype: 'Quality Review' },
      { label: 'Non conformances', doctype: 'Non Conformance' },
    ],
  },
  {
    title: 'Set up',
    entries: [
      { label: 'Company', doctype: 'Company' },
      { label: 'Currencies', doctype: 'Currency' },
      { label: 'Units of measure', doctype: 'UOM' },
      { label: 'Tax templates', doctype: 'Sales Taxes and Charges Template' },
      { label: 'Price lists', doctype: 'Price List' },
      { label: 'Terms', doctype: 'Terms and Conditions' },
      { label: 'People and access', screen: '/people', hint: 'Who can get in, and what they can reach' },
      { label: 'Industry packs', screen: '/packs', hint: 'The kinds of work your business runs on' },
      { label: 'Email accounts', doctype: 'Email Account', hint: 'Where mail is sent from and read into' },
      { label: 'Email templates', doctype: 'Email Template' },
      { label: 'Your website', screen: '/website', hint: 'Pages and forms the public can reach' },
      { label: 'Change a form', screen: '/customise', hint: 'Add a field your business needs' },
      { label: 'Approvals and alerts', screen: '/automation', hint: 'What needs approving, and who is told' },
    ],
  },
]

/** Every DocType a person may open, for Browse everything and the search box.
 *  Generated from a live site by `coverage/tools/survey.py` — never typed. */
export type IndexEntry = { n: string; m: string; s: 0 | 1; g: 0 | 1; t: 0 | 1; admin: 0 | 1 }

/* A Single has no table of its own: there is one record, it is named after
 * the DocType, and Frappe keeps its values in `tabSingles`. Asking Frappe to
 * *list* one is not an empty list, it is a missing table — `TableMissingError`,
 * which arrives here as a 500 and reaches a person as "that service is busy".
 * Sixty-three of the 468 openable DocTypes are Singles, so this was every
 * Settings screen in the product as well as the two the menu names.
 *
 * Which ones they are is a fact about the DocType and already in the bundle
 * (`g`, the flag Browse uses to caption them "settings"), so the right screen
 * is chosen before the first request rather than after a failed one. */
const SINGLES: Set<string> = new Set(
  (index as IndexEntry[]).filter((d) => d.g).map((d) => d.n))

export function isSingle(doctype: string): boolean {
  return SINGLES.has(doctype)
}

/** Where opening a DocType goes: its list, or — for a Single — the one
 *  record itself, whose name is the DocType's own. */
export function openRouteFor(doctype: string): string {
  const at = encodeURIComponent(doctype)
  return isSingle(doctype) ? `/doc/${at}/${at}` : `/list/${at}`
}

/** The plain-word label, where the menu gives one. */
export function labelFor(doctype: string): string {
  for (const section of MENU) {
    const hit = section.entries.find((e) => e.doctype === doctype)
    if (hit) return hit.label
  }
  return doctype
}

/** Where a menu entry goes. A report and a DocType are different routes, and
 *  an entry with neither is the index of all of them. */
export function routeFor(entry: Entry): string {
  if (entry.screen) return entry.screen
  if (entry.doctype) return openRouteFor(entry.doctype)
  if (entry.report) return `/report/${encodeURIComponent(entry.report)}`
  return '/reports'
}
